import React, { useState } from 'react';
import { ApiClient, API_BASE } from '../../api/client';
import { DEPLOYMENT, isOnHost } from '../../config/deployment';
import {
  AlertTriangle,
  ShieldCheck,
  Download,
  RotateCcw,
  CheckCircle2,
  Building2,
  FolderTree,
  Scale,
  Sparkles,
  X,
  Loader2,
  Lock
} from 'lucide-react';

interface DatabaseResetWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onResetComplete?: () => void;
}

export const DatabaseResetWizardModal: React.FC<DatabaseResetWizardModalProps> = ({
  isOpen,
  onClose,
  onResetComplete,
}) => {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const isLocalHost = host.includes('localhost') || host.includes('127.0.0.1');
  // S14: a VPS címe a konfigurációból jön (VITE_VPS_URL), nem a forráskódból.
  const isVps = host.includes('sslip.io') || isOnHost(DEPLOYMENT.vpsUrl);
  const isProduction = !isLocalHost && !isVps;

  // 1. Mentés állapot
  const [backupScope, setBackupScope] = useState<'full' | 'prod' | 'demo'>('full');
  const [isDownloadingBackup, setIsDownloadingBackup] = useState(false);
  const [backupDownloaded, setBackupDownloaded] = useState(false);

  // 2. Újratelepítési opciók
  const [seedDemo2026, setSeedDemo2026] = useState(true);
  const [seedRegulations, setSeedRegulations] = useState(true);
  const [provisionTemplates, setProvisionTemplates] = useState(true);
  const [selectedTenantSlugs, setSelectedTenantSlugs] = useState<string[]>([
    'akaszto-ovoda-demo',
    'balloszog-demo',
    'diosjeno-demo',
    'nagyigmand-demo',
    'nagyvenyim-demo',
    'pecsvarad-demo'
  ]);

  // Szervezetek listája a választáshoz
  const availableDemoTenants = [
    { slug: 'akaszto-ovoda-demo', name: 'Pacsirta Óvoda és Minibölcsőde (DEMO)', short: 'Akasztó Óvoda', color: 'from-rose-500 to-amber-500' },
    { slug: 'balloszog-demo', name: 'Ballószög Nagyközség Önkormányzata (DEMO)', short: 'Ballószög Önkormányzat', color: 'from-blue-600 to-emerald-500' },
    { slug: 'diosjeno-demo', name: 'Diósjenő Község Önkormányzata (DEMO)', short: 'Diósjenő Önkormányzat', color: 'from-emerald-600 to-blue-500' },
    { slug: 'nagyigmand-demo', name: 'Nagyigmánd Nagyközség Önkormányzata (DEMO)', short: 'Nagyigmánd Önkormányzat', color: 'from-purple-600 to-pink-500' },
    { slug: 'nagyvenyim-demo', name: 'Nagyvenyimi Polgármesteri Hivatal (DEMO)', short: 'Nagyvenyim Hivatal', color: 'from-sky-600 to-teal-500' },
    { slug: 'pecsvarad-demo', name: 'Pécsváradi Közös Önkormányzati Hivatal (DEMO)', short: 'Pécsvárad Hivatal', color: 'from-amber-600 to-red-600' }
  ];

  // 3. Megerősítés & végrehajtás
  const [confirmInput, setConfirmInput] = useState('');
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionError, setExecutionError] = useState<string | null>(null);
  const [resultData, setResultData] = useState<any | null>(null);

  if (!isOpen) return null;

  const handleDownloadBackup = async () => {
    setIsDownloadingBackup(true);
    try {
      const token = ApiClient.getToken();
      const res = await fetch(`${API_BASE}/admin/backup/export?scope=${backupScope}`, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (!res.ok) {
        throw new Error(`Szerver válasz: ${res.status} ${res.statusText}`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kozad_biztonsagi_mentes_${backupScope}_${new Date().toISOString().slice(0, 10)}.sql`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setBackupDownloaded(true);
    } catch (err: any) {
      alert('Hiba a mentés letöltése során: ' + (err.message || 'Ismeretlen hiba'));
    } finally {
      setIsDownloadingBackup(false);
    }
  };

  const toggleTenantSelection = (slug: string) => {
    if (selectedTenantSlugs.includes(slug)) {
      setSelectedTenantSlugs(selectedTenantSlugs.filter(s => s !== slug));
    } else {
      setSelectedTenantSlugs([...selectedTenantSlugs, slug]);
    }
  };

  const selectAllTenants = () => {
    setSelectedTenantSlugs(availableDemoTenants.map(t => t.slug));
  };

  const deselectAllTenants = () => {
    setSelectedTenantSlugs([]);
  };

  const handleExecuteReset = async () => {
    if (confirmInput.trim().toUpperCase() !== 'KINULLÁZÁS') {
      alert('A megerősítéshez írja be pontosan a KINULLÁZÁS szót!');
      return;
    }

    if (!window.confirm(
      'FIGYELEM! Ez a végső megerősítés!\n\nBiztosan elindítja az adatbázis kinullázását és a kiválasztott gyári komponensek újratelepítését?'
    )) {
      return;
    }

    setIsExecuting(true);
    setExecutionError(null);

    try {
      // 1. Reset és Seeding kérése
      const res = await ApiClient.resetDatabase({
        confirm_phrase: 'KINULLÁZÁS',
        seed_demo_2026: seedDemo2026,
        seed_regulations: seedRegulations,
        provision_tenant_ids: provisionTemplates ? 'all' : []
      });

      if (res.ok) {
        setResultData(res);
        if (onResetComplete) {
          onResetComplete();
        }
      } else {
        setExecutionError(res.error || 'Ismeretlen hiba történt a művelet során.');
      }
    } catch (err: any) {
      setExecutionError(err.message || 'Hálózati hiba történt a reset folyamatban.');
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md overflow-y-auto">
      <div className="relative w-full max-w-3xl my-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Fejléc */}
        <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-800 bg-gradient-to-r from-rose-50 via-slate-50 to-indigo-50 dark:from-rose-950/30 dark:via-slate-900 dark:to-indigo-950/30 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-rose-600 text-white shadow-md shadow-rose-500/20">
              <RotateCcw className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                  Adatbázis Kinullázása & Gyári Újrabetöltése
                </h2>
                <span className="px-2 py-0.5 rounded-md text-[10px] font-extrabold uppercase bg-rose-100 dark:bg-rose-900/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800">
                  SZUPERADMIN
                </span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Rendszertisztítás, szuperadminok védelme, 2026-os szervezetek és 101 jogszabályi pont betöltése
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isExecuting}
            className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Környezet jelző sáv */}
        <div className={`px-6 py-2.5 text-xs font-medium flex items-center justify-between border-b ${
          isProduction
            ? 'bg-rose-100 dark:bg-rose-950/60 text-rose-800 dark:text-rose-200 border-rose-200 dark:border-rose-900'
            : isVps
            ? 'bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-900'
            : 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-200 border-emerald-200 dark:border-emerald-900'
        }`}>
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                isProduction ? 'bg-rose-500' : isVps ? 'bg-amber-500' : 'bg-emerald-500'
              }`}></span>
              <span className={`relative inline-flex rounded-full h-2 w-2 ${
                isProduction ? 'bg-rose-600' : isVps ? 'bg-amber-600' : 'bg-emerald-600'
              }`}></span>
            </span>
            <span>
              {isProduction && '⚠️ Éles Felhőszerver (Cloudflare D1) aktív – Minden módosítás közvetlenül az éles adatokat érinti!'}
              {isVps && '🖥️ Saját VPS Szerver (sslip.io) aktív – A VPS helyi SQLite adatbázisára vonatkozik.'}
              {isLocalHost && '🏠 Helyi Fejlesztői Környezet (Localhost) aktív – Biztonságos tesztelés.'}
            </span>
          </div>
          <span className="font-mono text-[11px] opacity-80">{host}</span>
        </div>

        {/* Sikeres futás nézet */}
        {resultData ? (
          <div className="p-8 space-y-6">
            <div className="p-6 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-emerald-600 text-white flex items-center justify-center mx-auto shadow-lg shadow-emerald-600/20">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-emerald-900 dark:text-emerald-100">
                Az adatbázis sikeresen kinullázva és újraépítve!
              </h3>
              <p className="text-xs text-emerald-700 dark:text-emerald-300 max-w-lg mx-auto">
                A rendszer tiszta állapotba került, a szuperadmin felhasználók megmaradtak, a kiválasztott szervezetek és a közzétételi sablonok sikeresen legenerálódtak.
              </p>
            </div>

            {/* Statisztikai összefoglaló */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-800">
                <div className="text-xs text-slate-500 dark:text-slate-400">Megőrzött Szuperadmin</div>
                <div className="text-xl font-extrabold text-indigo-600 dark:text-indigo-400 mt-1">
                  {resultData.preservedSuperadmins?.length || 2} fő
                </div>
              </div>
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-800">
                <div className="text-xs text-slate-500 dark:text-slate-400">Betöltött Szervezet</div>
                <div className="text-xl font-extrabold text-blue-600 dark:text-blue-400 mt-1">
                  {resultData.seededOrgsCount || 6} db
                </div>
              </div>
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-800">
                <div className="text-xs text-slate-500 dark:text-slate-400">Jogszabály Törzs</div>
                <div className="text-xl font-extrabold text-emerald-600 dark:text-emerald-400 mt-1">
                  {resultData.seededRegsCount || 0} tétel
                </div>
              </div>
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-800">
                <div className="text-xs text-slate-500 dark:text-slate-400">Sablonnal Felépítve</div>
                <div className="text-xl font-extrabold text-purple-600 dark:text-purple-400 mt-1">
                  {resultData.provisionedTenantsCount || 0} szerv
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <button
                type="button"
                onClick={() => {
                  window.location.reload();
                }}
                className="px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs shadow-md transition cursor-pointer"
              >
                Kész & Felület Újratöltése
              </button>
            </div>
          </div>
        ) : (
          /* Műveleti lépések */
          <div className="p-6 space-y-6 overflow-y-auto max-h-[75vh]">
            {/* Hibaüzenet, ha volt */}
            {executionError && (
              <div className="p-4 rounded-xl bg-rose-50 dark:bg-rose-950/50 border border-rose-200 dark:border-rose-900 text-rose-800 dark:text-rose-200 text-xs flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 shrink-0" />
                <span>{executionError}</span>
              </div>
            )}

            {/* 1. LÉPÉS: BIZTONSÁGI MENTÉS */}
            <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-xs">1</span>
                  <h3 className="font-bold text-sm text-slate-900 dark:text-white">
                    Biztonsági Mentés Készítése (Erősen Ajánlott)
                  </h3>
                </div>
                {backupDownloaded && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 dark:bg-emerald-900/60 text-emerald-800 dark:text-emerald-200">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Mentés letöltve
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Mielőtt kinullázná az adatbázist, töltse le a jelenlegi állapotot. Alapértelmezetten a teljes mentést javasoljuk.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 pt-1">
                <label className={`p-3 rounded-xl border cursor-pointer text-xs flex items-center gap-2.5 transition ${
                  backupScope === 'full'
                    ? 'border-indigo-500 bg-indigo-50/60 dark:bg-indigo-950/40 font-semibold text-indigo-900 dark:text-indigo-200'
                    : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400'
                }`}>
                  <input
                    type="radio"
                    name="backup_scope"
                    checked={backupScope === 'full'}
                    onChange={() => setBackupScope('full')}
                    className="accent-indigo-600"
                  />
                  <span>🔵 Teljes Mentés (FULL)</span>
                </label>

                <label className={`p-3 rounded-xl border cursor-pointer text-xs flex items-center gap-2.5 transition ${
                  backupScope === 'prod'
                    ? 'border-emerald-500 bg-emerald-50/60 dark:bg-emerald-950/40 font-semibold text-emerald-900 dark:text-emerald-200'
                    : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400'
                }`}>
                  <input
                    type="radio"
                    name="backup_scope"
                    checked={backupScope === 'prod'}
                    onChange={() => setBackupScope('prod')}
                    className="accent-emerald-600"
                  />
                  <span>🟢 Csak Éles Adatok (PROD)</span>
                </label>

                <label className={`p-3 rounded-xl border cursor-pointer text-xs flex items-center gap-2.5 transition ${
                  backupScope === 'demo'
                    ? 'border-amber-500 bg-amber-50/60 dark:bg-amber-950/40 font-semibold text-amber-900 dark:text-amber-200'
                    : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400'
                }`}>
                  <input
                    type="radio"
                    name="backup_scope"
                    checked={backupScope === 'demo'}
                    onChange={() => setBackupScope('demo')}
                    className="accent-amber-600"
                  />
                  <span>🟡 Csak DEMO / Homokozó</span>
                </label>
              </div>

              <div className="pt-2">
                <button
                  type="button"
                  onClick={handleDownloadBackup}
                  disabled={isDownloadingBackup}
                  className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 dark:bg-slate-800 dark:hover:bg-slate-700 text-white font-medium text-xs flex items-center gap-2 transition cursor-pointer shadow-xs disabled:opacity-50"
                >
                  {isDownloadingBackup ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  <span>Biztonsági Mentés Letöltése (.sql)</span>
                </button>
              </div>
            </div>

            {/* 2. LÉPÉS: VÉDELEM & RESET BEÁLLÍTÁSOK */}
            <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 space-y-4">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-xs">2</span>
                <h3 className="font-bold text-sm text-slate-900 dark:text-white">
                  Kinullázás & Visszaállítandó Alapadatok
                </h3>
              </div>

              {/* Védelmi garanciák */}
              <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/60 flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                <div className="text-xs text-emerald-900 dark:text-emerald-200">
                  <span className="font-bold">Szuperadmin Fiókok Védelme:</span> A meglévő szuperadmin hozzáférések (és a központi <code className="bg-emerald-100 dark:bg-emerald-900 px-1 py-0.5 rounded font-mono">system</code> szervezet) automatikusan megmaradnak, a jelszavak és jogosultságok nem vesznek el.
                </div>
              </div>

              <div className="space-y-3">
                <label className="flex items-start gap-3 p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={seedDemo2026}
                    onChange={(e) => setSeedDemo2026(e.target.checked)}
                    className="mt-0.5 rounded accent-indigo-600"
                  />
                  <div>
                    <div className="text-xs font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5 text-indigo-600" />
                      2026-os Hivatalos DEMO Szervezetek & Felhasználók Betöltése
                    </div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                      6 db szervezet (Akasztó Óvoda, Ballószög, Diósjenő, Nagyigmánd, Nagyvenyim, Pécsvárad) és a hozzájuk tartozó 12 db felhasználó betöltése.
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-3 p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={seedRegulations}
                    onChange={(e) => setSeedRegulations(e.target.checked)}
                    className="mt-0.5 rounded accent-indigo-600"
                  />
                  <div>
                    <div className="text-xs font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                      <Scale className="w-3.5 h-3.5 text-blue-600" />
                      Hivatalos Jogszabálytár Betöltése (Infotv., Mötv., Ákr., Áht. stb.)
                    </div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                      A hatályos törvények és NJT.hu jogszabály-hivatkozások automatikus beillesztése a törzsadattárba.
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* 3. LÉPÉS: KÖZZÉTÉTELI MAPPASABLON BETÖLTÉSE (101 TÖRVÉNYI PONT) */}
            <div className="p-5 rounded-2xl bg-indigo-50/60 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-900/60 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-xs">3</span>
                  <h3 className="font-bold text-sm text-indigo-950 dark:text-indigo-100 flex items-center gap-2">
                    <FolderTree className="w-4 h-4 text-indigo-600" />
                    Közzétételi Mappastruktúra Sablon Betöltése (101 Törvényi Pont)
                  </h3>
                </div>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-200 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200">
                  SMART ZIP EQUIVALENT
                </span>
              </div>

              <label className="flex items-start gap-3 p-3 rounded-xl bg-white dark:bg-slate-900 border border-indigo-200 dark:border-indigo-900 cursor-pointer">
                <input
                  type="checkbox"
                  checked={provisionTemplates}
                  onChange={(e) => setProvisionTemplates(e.target.checked)}
                  className="mt-0.5 rounded accent-indigo-600"
                />
                <div>
                  <div className="text-xs font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                    Üres Mappasablon 101 pontjának legenerálása a szervezeteknél
                  </div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">
                    Pontosan olyan, mint amikor a Smart ZIP Importerben beimportálja a mappasablont: mind a 101 közzétételi pont legenerálódik, és a rendszer a szervezet <strong>saját adataira cseréli</strong> a változókat (név, cím, KRID, Hivatali Kapu, telefon, e-mail, honlap)!
                  </div>
                </div>
              </label>

              {provisionTemplates && (
                <div className="space-y-2 pt-1">
                  <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 px-1">
                    <span>Mely szervezetekhez generáljuk le a sablont?</span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={selectAllTenants}
                        className="text-indigo-600 dark:text-indigo-400 hover:underline font-semibold text-[11px]"
                      >
                        Összes kijelölése
                      </button>
                      <span>•</span>
                      <button
                        type="button"
                        onClick={deselectAllTenants}
                        className="text-slate-500 hover:underline text-[11px]"
                      >
                        Törlés
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {availableDemoTenants.map((t) => {
                      const isSelected = selectedTenantSlugs.includes(t.slug);
                      return (
                        <div
                          key={t.slug}
                          onClick={() => toggleTenantSelection(t.slug)}
                          className={`p-2.5 rounded-xl border text-xs cursor-pointer flex items-center justify-between transition ${
                            isSelected
                              ? 'border-indigo-500 bg-white dark:bg-slate-900 font-semibold text-slate-900 dark:text-white shadow-xs'
                              : 'border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 text-slate-500 dark:text-slate-400'
                          }`}
                        >
                          <div className="flex items-center gap-2 truncate pr-2">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {}}
                              className="rounded accent-indigo-600"
                            />
                            <span className="truncate">{t.short}</span>
                          </div>
                          <span className="text-[10px] text-slate-400 font-mono shrink-0">101 pont</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* 4. LÉPÉS: MEGERŐSÍTÉS ÉS INDÍTÁS */}
            <div className="p-5 rounded-2xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 space-y-4">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-rose-600 text-white flex items-center justify-center font-bold text-xs">4</span>
                <h3 className="font-bold text-sm text-rose-950 dark:text-rose-100 flex items-center gap-2">
                  <Lock className="w-4 h-4 text-rose-600" />
                  Megerősítés és Végrehajtás
                </h3>
              </div>

              <p className="text-xs text-rose-800 dark:text-rose-200 leading-relaxed">
                A művelet végleges törléssel jár! A folytatáshoz gépelje be csupa nagybetűvel a <strong className="font-mono bg-rose-100 dark:bg-rose-900/60 px-1.5 py-0.5 rounded">KINULLÁZÁS</strong> szót:
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={confirmInput}
                  onChange={(e) => setConfirmInput(e.target.value)}
                  placeholder="Írja be: KINULLÁZÁS"
                  className="flex-1 px-4 py-2.5 rounded-xl border border-rose-300 dark:border-rose-800 bg-white dark:bg-slate-900 font-mono text-sm tracking-wider text-rose-900 dark:text-rose-100 placeholder:text-rose-300 dark:placeholder:text-rose-800 focus:outline-none focus:ring-2 focus:ring-rose-500"
                />

                <button
                  type="button"
                  onClick={handleExecuteReset}
                  disabled={confirmInput.trim().toUpperCase() !== 'KINULLÁZÁS' || isExecuting}
                  className="px-6 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs shadow-md shadow-rose-600/20 transition flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer shrink-0"
                >
                  {isExecuting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Adatbázis újraépítése...</span>
                    </>
                  ) : (
                    <>
                      <RotateCcw className="w-4 h-4" />
                      <span>Adatbázis Kinullázása & Építése</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
